// contexts/ThemeContext.tsx
'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  isDarkMode: false,
  toggleTheme: () => { },
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Handle client-side mounting and initial state sync
  useEffect(() => {
    setMounted(true);

    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('jsonpatch-theme');
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const shouldBeDark = savedTheme === 'dark' || (!savedTheme && systemPrefersDark);

      if (shouldBeDark) {
        setIsDarkMode(true);
      }
    }
  }, []);

  // Apply theme to document
  const applyTheme = (dark: boolean) => {
    if (typeof window === 'undefined') return;
    const html = document.documentElement;

    if (dark) {
      html.classList.add('dark');
      html.classList.remove('light');
      // Set critical inline overrides for non-CSS-variable-aware properties
      html.style.setProperty('--bg', '#0b1120');
      html.style.setProperty('--bg-subtle', '#0f172a');
      html.style.setProperty('--bg-muted', '#1e293b');
      html.style.setProperty('--fg', '#f1f5f9');
      html.style.setProperty('--fg-muted', '#94a3b8');
    } else {
      html.classList.add('light');
      html.classList.remove('dark');
      html.style.setProperty('--bg', '#ffffff');
      html.style.setProperty('--bg-subtle', '#f8fafc');
      html.style.setProperty('--bg-muted', '#f1f5f9');
      html.style.setProperty('--fg', '#0f172a');
      html.style.setProperty('--fg-muted', '#475569');
    }
  };

  // Save theme to localStorage and update document class when it changes
  useEffect(() => {
    if (mounted && typeof window !== 'undefined') {
      localStorage.setItem('jsonpatch-theme', isDarkMode ? 'dark' : 'light');
      applyTheme(isDarkMode);
    }
  }, [isDarkMode, mounted]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const newMode = !prev;
      // Immediately update DOM to prevent flash
      applyTheme(newMode);
      return newMode;
    });
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
